import AXIOS from "@/axios";
import type { Area } from "react-easy-crop";

function getApiOrigin(): URL | null {
  const apiBaseUrl = import.meta.env.VITE_BASE_URL?.trim();
  if (!apiBaseUrl) return null;
  try {
    return new URL(apiBaseUrl.endsWith("/") ? apiBaseUrl : `${apiBaseUrl}/`);
  } catch {
    return null;
  }
}

function normalizeCropSourceUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.href);
    const apiOrigin = getApiOrigin();
    if (
      apiOrigin &&
      parsed.hostname === apiOrigin.hostname &&
      parsed.protocol === "http:" &&
      apiOrigin.protocol === "https:"
    ) {
      parsed.protocol = "https:";
      return parsed.toString();
    }
  } catch {
    return url;
  }
  return url;
}

function toAxiosRequestUrl(url: string): string {
  const normalized = normalizeCropSourceUrl(url);
  const apiOrigin = getApiOrigin();
  if (!apiOrigin) return normalized;

  try {
    const parsed = new URL(normalized, apiOrigin);
    if (parsed.origin === apiOrigin.origin) {
      const path = `${parsed.pathname}${parsed.search}`;
      return path.startsWith("/") ? path.slice(1) : path;
    }
  } catch {
    return normalized;
  }

  return normalized;
}

function isLocalImageSrc(url: string): boolean {
  return url.startsWith("data:") || url.startsWith("blob:");
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read image blob"));
    reader.readAsDataURL(blob);
  });
}

async function fetchRemoteImageAsDataUrl(url: string): Promise<string> {
  const requestUrl = toAxiosRequestUrl(url);
  const { data } = await AXIOS.get<Blob>(requestUrl, {
    responseType: "blob",
    skipGlobalErrorToast: true,
  });
  return blobToDataUrl(data);
}

/** Resolves remote crop sources via authenticated API fetch to avoid canvas CORS issues. */
export async function resolveCropImageSrc(url: string): Promise<string> {
  if (!url || isLocalImageSrc(url)) {
    return url;
  }

  return fetchRemoteImageAsDataUrl(url);
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", () =>
      reject(new Error(`Failed to load image: ${url}`))
    );
    if (!isLocalImageSrc(url)) {
      image.setAttribute("crossOrigin", "anonymous");
    }
    image.src = url;
  });
}

/** Returns a PNG data URL sized to the cropped pixel area (16:9 output from crop modal). */
export async function getCroppedImageDataUrl(
  imageSrc: string,
  pixelCrop: Area
): Promise<string> {
  const resolvedSrc = await resolveCropImageSrc(imageSrc);
  const image = await loadImage(resolvedSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is not supported");

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return canvas.toDataURL("image/png");
}
